import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatINR, formatApiError } from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { DownloadSimple, Target, Trophy } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const OVERVIEW = [
  ["visitors", "Website Visitors"], ["property_views", "Property Views"], ["searches", "Searches"],
  ["availability_checks", "Availability Checks"], ["booking_requests", "Booking Requests"], ["confirmed_bookings", "Confirmed Bookings"],
  ["payment_completed", "Payments Completed"], ["customer_leads", "Customer Leads"], ["owner_leads", "Owner Leads"],
  ["owner_registrations", "Owner Sign-ups"], ["properties_submitted", "Properties Submitted"], ["properties_approved", "Properties Approved"],
  ["whatsapp_clicks", "WhatsApp Clicks"], ["phone_clicks", "Phone Clicks"], ["property_shares", "Property Shares"], ["referral_registrations", "Referral Sign-ups"],
];
const FUNNEL = [
  ["visitor_to_view", "Visitor → Property View"], ["view_to_availability", "View → Availability Check"],
  ["availability_to_request", "Availability → Booking Request"], ["request_to_confirmed", "Request → Confirmed"],
  ["confirmed_to_paid", "Confirmed → Paid"], ["owner_lead_to_registration", "Owner Lead → Registration"],
  ["owner_registration_to_submitted", "Registration → Submitted"], ["submitted_to_approved", "Submitted → Approved"],
];
const CUST_SEG = [["new", "New"], ["repeat", "Repeat"], ["high_value", "High-value"], ["inactive", "Inactive"], ["farmhouse", "Farmhouse"], ["resort", "Resort"], ["banquet", "Banquet"], ["function", "Function"]];
const OWNER_SEG = [["new", "New"], ["pending", "Pending"], ["approved", "Approved"], ["active", "Active"], ["high_performing", "High-performing"], ["inactive", "Inactive"]];

const Progress = ({ pct }) => (
  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-stone-100">
    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
  </div>
);

export default function AdminMarketing() {
  const [data, setData] = useState(null);
  const [seg, setSeg] = useState(null);
  const [days, setDays] = useState("30");
  const [targets, setTargets] = useState(null);

  useEffect(() => { api.get("/admin/settings").then((r) => setTargets({
    target_properties: r.data.target_properties ?? 100, target_customers: r.data.target_customers ?? 1000,
    target_bookings: r.data.target_bookings ?? 100, target_farmhouses: r.data.target_farmhouses ?? 40,
    target_resorts: r.data.target_resorts ?? 20, target_banquet_halls: r.data.target_banquet_halls ?? 20,
    target_function_halls: r.data.target_function_halls ?? 20,
  })).catch(() => {}); }, []);
  const saveTargets = async () => {
    const payload = Object.fromEntries(Object.entries(targets).map(([k, v]) => [k, Math.max(1, Number(v) || 1)]));
    try {
      await api.put("/admin/settings", { settings: payload });
      toast.success("Targets updated");
      api.get(`/admin/marketing?days=${days}`).then((r) => setData(r.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const setT = (k) => (e) => setTargets({ ...targets, [k]: e.target.value });

  useEffect(() => { setData(null); api.get(`/admin/marketing?days=${days}`).then((r) => setData(r.data)); }, [days]);
  useEffect(() => { api.get("/admin/segments").then((r) => setSeg(r.data)); }, []);

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Metric", "Value"], ...OVERVIEW.map(([k, l]) => [l, data.overview[k] ?? 0]),
      ...FUNNEL.map(([k, l]) => [l, `${data.conversions[k] ?? 0}%`])];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `avinya-marketing-${days}d.csv`; a.click();
  };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Marketing</h1>
        <div className="flex items-center gap-2">
          <Button data-testid="export-csv" variant="outline" onClick={exportCsv} className="gap-1 rounded-full"><DownloadSimple size={16} /> CSV</Button>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger data-testid="marketing-range" className="w-40 rounded-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem><SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem><SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {data === null ? <div className="flex justify-center py-20"><Spinner /></div> : (
        <>
          {/* KPI trackers */}
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div data-testid="kpi-properties" className="rounded-2xl border border-stone-200 bg-white p-5">
              <div className="flex items-center gap-2"><Target size={20} weight="duotone" className="text-primary" /><p className="font-heading font-semibold">First {data.kpi.properties.target} Properties</p></div>
              <p className="mt-2 font-heading text-3xl font-bold text-stone-900">{data.kpi.properties.current}<span className="text-base font-medium text-stone-400"> / {data.kpi.properties.target}</span></p>
              <Progress pct={data.kpi.properties.pct} />
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-stone-500">{Object.entries(data.kpi.properties.breakdown).map(([k, v]) => <span key={k} className="rounded-full bg-stone-100 px-2 py-0.5">{k}: {v}</span>)}</div>
            </div>
            <div data-testid="kpi-bookings" className="rounded-2xl border border-stone-200 bg-white p-5">
              <div className="flex items-center gap-2"><Trophy size={20} weight="duotone" className="text-amber-500" /><p className="font-heading font-semibold">First {Number(data.kpi.bookings.target).toLocaleString("en-IN")} Bookings</p></div>
              <p className="mt-2 font-heading text-3xl font-bold text-stone-900">{data.kpi.bookings.current}<span className="text-base font-medium text-stone-400"> / {data.kpi.bookings.target}</span></p>
              <Progress pct={data.kpi.bookings.pct} />
              <p className="mt-3 text-xs text-stone-400">{data.kpi.bookings.target - data.kpi.bookings.current} confirmed bookings to go</p>
            </div>
          </div>

          {/* Targets editor */}
          {targets && (
            <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5" data-testid="targets-editor">
              <p className="font-heading font-semibold text-stone-900">Launch targets <span className="text-xs font-normal text-stone-400">(internal goals)</span></p>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                {[["Properties", "target_properties"], ["Customers", "target_customers"], ["Bookings", "target_bookings"], ["Farmhouses", "target_farmhouses"], ["Resorts", "target_resorts"], ["Banquet Halls", "target_banquet_halls"], ["Function Halls", "target_function_halls"]].map(([l, k]) => (
                  <div key={k}>
                    <label className="text-[11px] text-stone-500">{l}</label>
                    <input data-testid={`target-${k}`} type="number" value={targets[k]} onChange={setT(k)} className="mt-1 w-full rounded-lg border border-stone-200 px-2 py-1.5 text-sm" />
                  </div>
                ))}
              </div>
              <Button data-testid="save-targets" onClick={saveTargets} className="mt-3 rounded-xl bg-primary hover:bg-green-900">Save targets</Button>
            </div>
          )}

          {/* Overview */}
          <h2 className="mt-8 font-heading text-lg font-semibold text-stone-900">Overview ({days === "1" ? "today" : `last ${days} days`})</h2>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            {OVERVIEW.map(([key, label]) => (
              <div key={key} data-testid={`metric-${key}`} className="rounded-2xl border border-stone-200 bg-white p-4">
                <p className="font-heading text-2xl font-bold text-stone-900">{data.overview[key] ?? 0}</p>
                <p className="text-xs text-stone-500">{label}</p>
              </div>
            ))}
          </div>

          {/* Conversion funnel */}
          <h2 className="mt-8 font-heading text-lg font-semibold text-stone-900">Conversion Rates</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {FUNNEL.map(([key, label]) => (
              <div key={key} className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="flex items-center justify-between"><span className="text-sm text-stone-600">{label}</span><span className="font-heading font-bold text-primary">{data.conversions[key] ?? 0}%</span></div>
                <Progress pct={Math.min(data.conversions[key] ?? 0, 100)} />
              </div>
            ))}
          </div>

          {/* Top categories & locations */}
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
              <p className="font-heading font-semibold">Top Categories</p>
              <div className="mt-3 space-y-2">
                {data.top_categories.map((c) => (
                  <div key={c.category} className="flex items-center justify-between text-sm"><span className="text-stone-700">{c.category}</span><span className="text-stone-500">{c.bookings} bookings · {formatINR(c.value)}</span></div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
              <p className="font-heading font-semibold">Top Locations</p>
              <div className="mt-3 space-y-2">
                {data.top_locations.map((l) => (
                  <div key={l.location} className="flex items-center justify-between text-sm"><span className="text-stone-700">{l.location}</span><span className="text-stone-500">{l.bookings} bookings · {formatINR(l.value)}</span></div>
                ))}
              </div>
            </div>
          </div>

          {/* Segments */}
          {seg && (
            <>
              <h2 className="mt-8 font-heading text-lg font-semibold text-stone-900">Segments</h2>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div data-testid="cust-segments" className="rounded-2xl border border-stone-200 bg-white p-5">
                  <p className="mb-3 font-heading font-semibold">Customers <span className="text-stone-400">({seg.totals.customers})</span></p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {CUST_SEG.map(([k, l]) => <div key={k} className="rounded-xl bg-stone-50 p-2.5 text-center"><p className="font-heading text-lg font-bold text-stone-900">{seg.customers[k] ?? 0}</p><p className="text-[10px] text-stone-400">{l}</p></div>)}
                  </div>
                </div>
                <div data-testid="owner-segments" className="rounded-2xl border border-stone-200 bg-white p-5">
                  <p className="mb-3 font-heading font-semibold">Owners <span className="text-stone-400">({seg.totals.owners})</span></p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {OWNER_SEG.map(([k, l]) => <div key={k} className="rounded-xl bg-stone-50 p-2.5 text-center"><p className="font-heading text-lg font-bold text-stone-900">{seg.owners[k] ?? 0}</p><p className="text-[10px] text-stone-400">{l}</p></div>)}
                  </div>
                </div>
              </div>
            </>
          )}
          <p className="mt-6 text-xs text-stone-400">Metrics are based on real, in-app tracked events. Connect Google Analytics later for deeper insights. KPI targets are internal business goals, not public claims.</p>
        </>
      )}
    </DashboardLayout>
  );
}
